import { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Users, Search, AlertCircle, AlertTriangle, CheckCircle2,
  BadgeCheck, ChevronRight, ArrowLeft,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Button, Input, Spinner, EmptyState } from "@/components/ui";
import { expiryBand } from "./licenceHelpers";

// =============================================================================
// HrLicencesScreen — /hr/licences
// Cross-team director/manager board. Rows = employees, with licence summary
// counts + click-through to /hr/licences/:employee_id for the detail view.
// Filter chips for common management tasks (pending verify / expiring /
// overdue). T-004 Commit D.
// =============================================================================

type FilterKey = "all" | "pending" | "warning" | "overdue";

interface EmployeeRow {
  employee_id: string;
  first_name:  string | null;
  last_name:   string | null;
  email:       string | null;
  primary_role_name: string | null;
  is_active:   boolean;
}

interface LicenceMini {
  licence_id:      string;
  employee_id:     string;
  expiry_date:     string | null;
  verified_at:     string | null;
  licence_type: {
    display_name:          string;
    requires_verification: boolean;
  } | null;
}

interface EmployeeSummary {
  employee: EmployeeRow;
  total:    number;
  pending:  number;   // requires_verification + !verified_at
  warning:  number;   // expiring ≤60d
  overdue:  number;
}

export default function HrLicencesScreen() {
  const user = useRequireAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");

  const filter = (searchParams.get("filter") as FilterKey) || "all";
  const setFilter = (f: FilterKey) => {
    if (f === "all") setSearchParams({});
    else setSearchParams({ filter: f });
  };

  // Auth gate — this is a director/manager page. Shouldn't even be routed to
  // by non-mgr, but belt-and-braces.
  const canView = hasRole(user, "manager");

  // -------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------
  const { data: employees, isLoading: empLoading } = useQuery({
    queryKey: ["hr_licences_employees"],
    enabled: canView,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_employees")
        .select("employee_id, first_name, last_name, email, primary_role_name, is_active")
        .eq("is_active", true)
        .order("last_name");
      if (error) throw error;
      return (data ?? []) as unknown as EmployeeRow[];
    },
  });

  const { data: licences, isLoading: licLoading } = useQuery({
    queryKey: ["hr_licences_all"],
    enabled: canView,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employee_licences")
        .select(`
          licence_id, employee_id, expiry_date, verified_at,
          licence_type:licence_types (display_name, requires_verification)
        `)
        .eq("is_active", true);
      if (error) throw error;
      return (data ?? []) as unknown as LicenceMini[];
    },
  });

  // -------------------------------------------------------------------------
  // Aggregate per employee
  // -------------------------------------------------------------------------
  const summaries: EmployeeSummary[] = useMemo(() => {
    if (!employees || !licences) return [];

    const byEmp = new Map<string, LicenceMini[]>();
    for (const l of licences) {
      if (!byEmp.has(l.employee_id)) byEmp.set(l.employee_id, []);
      byEmp.get(l.employee_id)!.push(l);
    }

    return employees.map(e => {
      const list = byEmp.get(e.employee_id) ?? [];
      const overdue = list.filter(l => expiryBand(l.expiry_date) === "overdue").length;
      const warning = list.filter(l => expiryBand(l.expiry_date) === "warning").length;
      const pending = list.filter(l =>
        l.licence_type?.requires_verification && !l.verified_at
      ).length;
      return { employee: e, total: list.length, pending, warning, overdue };
    });
  }, [employees, licences]);

  // -------------------------------------------------------------------------
  // Apply filter + search
  // -------------------------------------------------------------------------
  const filtered = useMemo(() => {
    let out = summaries;

    if (filter === "pending") out = out.filter(s => s.pending > 0);
    if (filter === "warning") out = out.filter(s => s.warning > 0);
    if (filter === "overdue") out = out.filter(s => s.overdue > 0);

    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter(s => {
        const name = `${s.employee.first_name ?? ""} ${s.employee.last_name ?? ""}`.toLowerCase();
        const email = (s.employee.email ?? "").toLowerCase();
        return name.includes(q) || email.includes(q);
      });
    }

    return out;
  }, [summaries, filter, search]);

  // Roll-up counts for the filter chip badges
  const totalPending = summaries.reduce((a, s) => a + s.pending, 0);
  const totalWarning = summaries.reduce((a, s) => a + s.warning, 0);
  const totalOverdue = summaries.reduce((a, s) => a + s.overdue, 0);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  if (!canView) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <div className="p-6 text-text-muted text-sm">
            Licence oversight is restricted to managers and directors.
          </div>
        </Card>
      </div>
    );
  }

  const loading = empLoading || licLoading;

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto">
      <Link to="/hr" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3">
        <ArrowLeft size={14}/> HR hub
      </Link>
      <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Licence oversight</h1>
          <div className="text-text-muted text-[13px] mt-1">
            Every employee's licences in one place. Click a row for the detail view.
          </div>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <FilterChip
          label="All"        count={summaries.length}
          active={filter === "all"}
          onClick={() => setFilter("all")}
        />
        <FilterChip
          label="Pending verification" count={totalPending}
          active={filter === "pending"}
          onClick={() => setFilter("pending")}
          tone={totalPending > 0 ? "warn" : undefined}
        />
        <FilterChip
          label="Expiring soon" count={totalWarning}
          active={filter === "warning"}
          onClick={() => setFilter("warning")}
          tone={totalWarning > 0 ? "warn" : undefined}
        />
        <FilterChip
          label="Overdue"   count={totalOverdue}
          active={filter === "overdue"}
          onClick={() => setFilter("overdue")}
          tone={totalOverdue > 0 ? "danger" : undefined}
        />

        <div className="flex items-center gap-2 px-3 py-1.5 bg-ink-raised border border-line rounded-md flex-1 max-w-md ml-auto">
          <Search size={14} className="text-text-muted" />
          <input
            placeholder="Search name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-transparent outline-none text-text text-[13px] flex-1"
          />
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Spinner size={20} /></div>
      ) : filtered.length === 0 ? (
        <Card>
          <div className="p-6">
            <EmptyState
              title={
                filter === "all"
                  ? "No employees with licences to show"
                  : `No employees match the "${filter}" filter`
              }
              icon={Users}
            />
          </div>
        </Card>
      ) : (
        <Card>
          <table className="w-full">
            <thead>
              <tr className="border-b border-line text-left text-[10px] font-semibold text-text-muted uppercase tracking-wider2">
                <th className="px-4 py-3">Employee</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3 text-center">Total</th>
                <th className="px-4 py-3 text-center">Pending</th>
                <th className="px-4 py-3 text-center">Expiring</th>
                <th className="px-4 py-3 text-center">Overdue</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <EmployeeSummaryRow key={s.employee.employee_id} summary={s} />
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface FilterChipProps {
  label:    string;
  count:    number;
  active:   boolean;
  onClick:  () => void;
  tone?:    "warn" | "danger";
}

function FilterChip({ label, count, active, onClick, tone }: FilterChipProps) {
  const toneClass = !active && tone === "danger" ? "text-danger"
                  : !active && tone === "warn"   ? "text-warn"
                  : "";
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded text-[11px] font-mono uppercase tracking-wider2 font-semibold border transition-colors ${
        active
          ? "bg-accent/12 text-accent border-accent/30"
          : `bg-ink-raised ${toneClass || "text-text-muted"} border-line hover:text-text`
      }`}
    >
      {label}
      {count > 0 && <span className="ml-1.5 opacity-70">{count}</span>}
    </button>
  );
}

// ---------------------------------------------------------------------------

function EmployeeSummaryRow({ summary }: { summary: EmployeeSummary }) {
  const { employee: e, total, pending, warning, overdue } = summary;
  const name = `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim() || "(no name)";

  return (
    <tr className="border-b border-line last:border-b-0 hover:bg-ink-hover transition-colors">
      <td className="px-4 py-3">
        <Link to={`/hr/licences/${e.employee_id}`} className="block">
          <div className="font-medium text-[13px]">{name}</div>
          <div className="text-[11px] text-text-muted font-mono mt-0.5">
            {e.email ?? "—"}
          </div>
        </Link>
      </td>
      <td className="px-4 py-3 text-[12px] text-text-muted capitalize">
        {e.primary_role_name?.replace(/_/g, " ") ?? "—"}
      </td>
      <td className="px-4 py-3 text-center">
        <span className="font-semibold text-[13px]">{total}</span>
      </td>
      <td className="px-4 py-3 text-center">
        {pending > 0
          ? <Pill label={String(pending)} tone="warning" small />
          : <span className="text-text-muted text-[12px]">—</span>}
      </td>
      <td className="px-4 py-3 text-center">
        {warning > 0
          ? <Pill label={String(warning)} tone="warning" small />
          : <span className="text-text-muted text-[12px]">—</span>}
      </td>
      <td className="px-4 py-3 text-center">
        {overdue > 0
          ? <Pill label={String(overdue)} tone="rejected" small />
          : <span className="text-text-muted text-[12px]">—</span>}
      </td>
      <td className="px-4 py-3 text-right">
        <Link to={`/hr/licences/${e.employee_id}`}>
          <Button variant="ghost" size="sm">Open <ChevronRight size={12}/></Button>
        </Link>
      </td>
    </tr>
  );
}
